import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Eye } from 'lucide-react';

interface EmailComposerProps {
  subject: string;
  htmlContent: string;
  onSubjectChange: (subject: string) => void;
  onContentChange: (content: string) => void;
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  subject,
  htmlContent,
  onSubjectChange,
  onContentChange,
}) => {
  const [editorMode, setEditorMode] = useState<'visual' | 'raw'>('visual');
  const [showModeWarning, setShowModeWarning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Update iframe content when preview is shown
  useEffect(() => {
    if (showPreview && iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(
          htmlContent ||
            '<p style="color: #999; text-align: center; padding: 40px;">No content to preview</p>'
        );
        doc.close();
      }
    }
  }, [showPreview, htmlContent]);

  // Auto-detect complex HTML and suggest raw mode
  useEffect(() => {
    if (htmlContent) {
      const isComplexHTML =
        htmlContent.includes('<!DOCTYPE') ||
        htmlContent.includes('<html') ||
        htmlContent.includes('style=') ||
        htmlContent.includes('<figure') ||
        htmlContent.includes('class=') ||
        htmlContent.trim().startsWith('<table');

      if (isComplexHTML && editorMode === 'visual') {
        // Suggest raw mode but don't auto-switch
        setShowModeWarning(true);
      }
    }
  }, [htmlContent, editorMode]);

  const handleModeSwitch = (newMode: 'visual' | 'raw') => {
    if (editorMode === 'raw' && newMode === 'visual' && htmlContent) {
      // Check if content looks like it has complex HTML
      const hasComplexHTML =
        htmlContent.includes('<!DOCTYPE') ||
        htmlContent.includes('<html') ||
        htmlContent.includes('style=') ||
        htmlContent.includes('<figure');

      if (hasComplexHTML) {
        const confirmed = window.confirm(
          'Switching to Visual Editor may modify your HTML content (remove inline styles, wrap tables, etc.). Continue?'
        );
        if (!confirmed) {
          return;
        }
      }
    }
    setEditorMode(newMode);
    setShowModeWarning(false);
  };

  const editorConfiguration = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'blockQuote',
      'insertTable',
      'undo',
      'redo',
    ],
    placeholder: 'Write your newsletter content here...',
  };

  return (
    <div className="email-composer">
      <Form.Group className="mb-3">
        <Form.Label className="fw-600">Subject Line</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter email subject... (e.g., Welcome {{first_name}}!)"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="subject-input"
        />
        <Form.Text className="text-muted">
          You can use <code>{`{{first_name}}`}</code> and{' '}
          <code>{`{{last_name}}`}</code> for personalization
        </Form.Text>
      </Form.Group>

      <Form.Group>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="fw-600 mb-0">Email Body (HTML)</Form.Label>
          <div
            className="btn-group"
            role="group"
            aria-label="Editor mode selection"
          >
            <Button
              variant={editorMode === 'visual' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleModeSwitch('visual')}
              aria-pressed={editorMode === 'visual'}
            >
              Visual Editor
            </Button>
            <Button
              variant={editorMode === 'raw' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleModeSwitch('raw')}
              aria-pressed={editorMode === 'raw'}
            >
              Raw HTML
            </Button>
          </div>
        </div>

        {showModeWarning && editorMode === 'visual' && (
          <Alert
            variant="warning"
            className="mb-3"
            dismissible
            onClose={() => setShowModeWarning(false)}
          >
            <strong>⚠️ Complex HTML Detected:</strong> Your content appears to
            contain complex HTML with inline styles or full HTML structure.
            Consider switching to <strong>Raw HTML</strong> mode to preserve
            formatting and prevent CKEditor from modifying your code.
          </Alert>
        )}

        {editorMode === 'raw' ? (
          <div className="raw-html-container">
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPreview(true)}
                disabled={!htmlContent.trim()}
              >
                <Eye size={16} className="me-1" />
                Preview
              </Button>
            </div>
            <Form.Control
              as="textarea"
              rows={15}
              value={htmlContent}
              onChange={(e) => onContentChange(e.target.value)}
              placeholder="Enter your HTML content here... (e.g., &lt;h1&gt;Hello {{first_name}}!&lt;/h1&gt;)"
              style={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                minHeight: '400px',
              }}
            />
            <Form.Text className="text-muted mt-2 d-block">
              💡 <strong>Raw HTML Mode:</strong> Edit HTML directly without
              modification. Use <code>{`{{first_name}}`}</code> and{' '}
              <code>{`{{last_name}}`}</code> for personalization. Perfect for
              email templates with inline CSS styles.
            </Form.Text>
          </div>
        ) : (
          <div className="editor-container">
            <CKEditor
              editor={ClassicEditor as any}
              data={htmlContent}
              config={editorConfiguration}
              onChange={(event, editor) => {
                const data = editor.getData();
                onContentChange(data);
              }}
              onReady={(editor) => {
                // Set editor height
                const root = editor.editing.view.document.getRoot();
                if (root) {
                  editor.editing.view.change((writer) => {
                    writer.setStyle('min-height', '400px', root);
                  });
                }
              }}
            />
            <Form.Text className="text-muted mt-2 d-block">
              💡 <strong>Personalization:</strong> Use{' '}
              <code>{`{{first_name}}`}</code> and <code>{`{{last_name}}`}</code>{' '}
              in your content. They will be automatically replaced with each
              recipient's name when sending.
            </Form.Text>
          </div>
        )}
      </Form.Group>

      {/* HTML Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="xl"
        centered
        aria-labelledby="preview-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="preview-modal-title">
            <Eye size={20} className="me-2" aria-hidden="true" />
            Email Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, backgroundColor: '#f1f5f9' }}>
          <div className="text-center py-2 bg-light border-bottom">
            <small className="text-muted">
              This preview shows how your email will render. Actual appearance
              may vary slightly across email clients.
            </small>
          </div>
          <div style={{ height: '70vh', overflow: 'auto' }}>
            <iframe
              ref={iframeRef}
              title="Email Preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#ffffff',
              }}
              sandbox="allow-same-origin"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailComposer;
